import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, User, Lock, LogIn } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import styles from './Login.module.css';

interface FormErrors {
  identifier?: string;
  password?: string;
  global?: string;
}

export function LoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const login = useAuthStore((s) => s.login);
  const clearError = useAuthStore((s) => s.clearError);
  const navigate = useNavigate();

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!identifier.trim()) {
      newErrors.identifier = 'Введите логин';
    }

    if (!password) {
      newErrors.password = 'Введите пароль';
    } else if (password.length < 6) {
      newErrors.password = 'Пароль должен содержать минимум 6 символов';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    clearError();

    if (!validate()) return;

    setIsLoading(true);

    try {
      await login(identifier, password);
      navigate('/cameras');
    } catch (error) {
      setErrors({
        global: error instanceof Error
          ? error.message
          : 'Произошла ошибка при входе'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <div className={styles.logo}>
              <Video size={32} />
            </div>
            <h1 className={styles.title}>VidWatch</h1>
            <p className={styles.subtitle}>Система видеонаблюдения</p>
          </div>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            {errors.global && (
              <div className={styles.globalError} role="alert">
                {errors.global}
              </div>
            )}

            <div className={styles.field}>
              <label htmlFor="identifier" className={styles.label}>
                Логин
              </label>
              <div className={styles.inputWrapper}>
                <User size={18} className={styles.inputIcon} />
                <input
                  id="identifier"
                  type="text"
                  className={`${styles.input} ${errors.identifier ? styles.inputError : ''}`}
                  placeholder="Введите логин"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  disabled={isLoading}
                  autoComplete="username"
                  autoFocus
                />
              </div>
              {errors.identifier && (
                <span className={styles.errorText}>{errors.identifier}</span>
              )}
            </div>

            <div className={styles.field}>
              <label htmlFor="password" className={styles.label}>
                Пароль
              </label>
              <div className={styles.inputWrapper}>
                <Lock size={18} className={styles.inputIcon} />
                <input
                  id="password"
                  type="password"
                  className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                  placeholder="Введите пароль"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              </div>
              {errors.password && (
                <span className={styles.errorText}>{errors.password}</span>
              )}
            </div>

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className={styles.spinner} />
                  Вход...
                </>
              ) : (
                <>
                  <LogIn size={18} />
                  Войти
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
